import { UsersQueryService, type UserModel } from '@supply-mover-2-0/core';
import type React from 'react';
import { useCallback } from 'react';

import { useTranslation } from '../../../providers/TranslationProvider';
import { EntitySelectorBase } from './EntitySelectorBase';
import type { UserSelectorProps } from './types';

export const UserSelector = ({
	value,
	onChange,
	placeholder,
	disabled,
	error,
	allowClear,
	className,
	excludeIds,
}: UserSelectorProps): React.JSX.Element => {
	const { t } = useTranslation();

	const loadItems = useCallback(
		async (query: string): Promise<Array<UserModel>> => {
			const users = await UsersQueryService.getUsers(query);

			return users;
		},
		[],
	);

	const loadItemById = useCallback(
		async (id: number): Promise<UserModel | null> => {
			const result = await UsersQueryService.getUserByInstanceId(id);

			return result.data ?? null;
		},
		[],
	);

	const mapToOption = useCallback(
		(user: UserModel): { id: number; label: string; subtitle?: string } => {
			const nameParts = [user.firstName, user.lastName].filter(Boolean);
			const fullName =
				nameParts.length > 0 ? nameParts.join(' ') : t('Unknown User');

			return {
				id: user.id,
				label: fullName,
				subtitle: user.email,
			};
		},
		[t],
	);

	return (
		<EntitySelectorBase<UserModel>
			value={value}
			onChange={onChange}
			placeholder={placeholder ?? t('Search users...')}
			disabled={disabled}
			error={error}
			allowClear={allowClear}
			className={className}
			loadItems={loadItems}
			loadItemById={loadItemById}
			mapToOption={mapToOption}
			excludeIds={excludeIds}
		/>
	);
};
